import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { STEP_META, wizardProgressPercent } from '../utils/registerWizardModel';

const { semantic } = colorTokens;

/**
 * Figma-style wizard header: Step X / 6, title, subtitle, progress bar.
 */
export function RegisterWizardHeader({
  stepKey,
  title,
  subtitle,
  technicalProgressPercent = null,
  wizardProgressLabel,
  technicalProgressLabel,
  stepLabel,
  testId = 'register-wizard-header',
}) {
  const meta = STEP_META[stepKey] || { index: 1, total: 6 };
  const wizardPercent = wizardProgressPercent(stepKey);
  const showTechnical = stepKey === 'provisioning'
    && typeof technicalProgressPercent === 'number';
  const resolvedStepLabel = stepLabel || `Step ${meta.index} / ${meta.total}`;
  const wizardProgressName = String(wizardProgressLabel || resolvedStepLabel || '').trim();
  const technicalProgressName = String(
    technicalProgressLabel || wizardProgressName || resolvedStepLabel || '',
  ).trim();

  return (
    <Box data-testid={testId} sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontFamily: typographyTokens.fontFamily,
          fontSize: 12,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: semantic.text.muted,
          mb: 0.75,
        }}
        data-testid="register-wizard-step-label"
      >
        {resolvedStepLabel}
      </Typography>
      {title ? (
        <Typography
          component="h2"
          sx={{
            fontFamily: typographyTokens.fontFamilyDisplay,
            fontWeight: typographyTokens.fontWeightBold,
            fontSize: { xs: 20, md: 24 },
            color: semantic.text.primary,
            mb: subtitle ? 0.75 : 1.5,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 14,
            color: semantic.text.secondary,
            mb: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}

      <Stack spacing={1}>
        <Box>
          {showTechnical && wizardProgressLabel ? (
            <Typography sx={{ fontSize: 12, color: semantic.text.muted, mb: 0.5 }}>
              {wizardProgressLabel}
            </Typography>
          ) : null}
          <LinearProgress
            variant="determinate"
            value={wizardPercent}
            aria-label={wizardProgressName}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={wizardPercent}
            data-testid="register-wizard-progress"
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: semantic.action.secondary.bg,
              '& .MuiLinearProgress-bar': { bgcolor: semantic.status.brand.border },
            }}
          />
        </Box>
        {showTechnical ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
                {technicalProgressLabel}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: semantic.text.secondary }}
                data-testid="register-wizard-tech-progress-value"
              >
                {Math.round(technicalProgressPercent)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={technicalProgressPercent}
              aria-label={technicalProgressName}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={technicalProgressPercent}
              data-testid="register-wizard-tech-progress"
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: semantic.action.secondary.bg,
                '& .MuiLinearProgress-bar': { bgcolor: semantic.status.brand.border },
              }}
            />
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

export default RegisterWizardHeader;
